import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "emailjs-com";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import { ArrowRight, Check, LinkedIn, Mail } from "../ui/Icons";
import { LINKEDIN_URL, CONTACT_EMAIL } from "../../data/site";
import "./Contact.css";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_zwfup4t";
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_d4us1q6";
const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || "yKrpJux-7EMz6TfYb";
const RECAPTCHA_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LfMONsqAAAAAC2-xMP4wyFStGzCSyUlP-K1HXCH";

const FIELDS = [
  { name: "firstName", label: "First name", autoComplete: "given-name", rules: { required: "Required" } },
  { name: "lastName", label: "Last name", autoComplete: "family-name", rules: { required: "Required" } },
  {
    name: "email",
    label: "Work email",
    type: "email",
    autoComplete: "email",
    full: true,
    rules: {
      required: "Required",
      pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Enter a valid email" },
    },
  },
  { name: "company", label: "Company", autoComplete: "organization" },
  { name: "telephone", label: "Telephone", type: "tel", autoComplete: "tel" },
  {
    name: "message",
    label: "What are you trying to see?",
    placeholder: "Cameras, site, the problem you want solved…",
    multiline: true,
    full: true,
    rules: { required: "Required" },
  },
];

const NEXT = [
  { title: "Discovery call", text: "30 minutes on your cameras, sites and the outcomes you need." },
  { title: "Site & feed assessment", text: "We review sample footage and pick models, zones and hardware." },
  { title: "Pilot on your hardware", text: "A scoped pilot on an edge device or in the cloud, with real metrics." },
];

export default function Contact() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });
  const [captcha, setCaptcha] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const captchaRef = useRef(null);
  const sending = status.state === "sending";

  const onSubmit = async (data) => {
    setStatus({ state: "sending", message: "" });
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          company: data.company,
          telephone: data.telephone,
          message: data.message,
        },
        USER_ID,
      );
      reset();
      setCaptcha(false);
      captchaRef.current?.reset();
      setStatus({ state: "success", message: "Thanks, your message is on its way. We reply within one business day." });
    } catch (err) {
      setStatus({ state: "error", message: "Sending failed. Please try again or email us directly." });
    }
  };

  return (
    <section id="contact" className="section ct">
      <div className="ct__glow" aria-hidden="true" />
      <div className="container ct__grid">
        <div className="ct__copy">
          <SectionHead
            eyebrow="Get in touch"
            title="Let's put intelligence behind your cameras."
            lead="Tell us about your site and what you need to see. We'll come back with a concrete proposal, hardware included."
          />
          <ol className="next">
            {NEXT.map((n, i) => (
              <Reveal as="li" key={n.title} className="next__item" delay={i * 0.08}>
                <span className="next__idx mono">0{i + 1}</span>
                <div>
                  <h3 className="next__title">{n.title}</h3>
                  <p className="next__text">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </ol>
          <div className="ct__links">
            <a className="ct__link" href={`mailto:${CONTACT_EMAIL}`}>
              <Mail /> {CONTACT_EMAIL}
            </a>
            <a className="ct__link" href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
              <LinkedIn /> LinkedIn
            </a>
          </div>
        </div>

        <Reveal className="form card" delay={0.1}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="fgrid">
              {FIELDS.map((f) => {
                const err = errors[f.name];
                const common = {
                  id: `ct-${f.name}`,
                  placeholder: f.placeholder || " ",
                  autoComplete: f.autoComplete,
                  "aria-invalid": err ? "true" : "false",
                  ...register(f.name, f.rules),
                };
                return (
                  <div
                    key={f.name}
                    className={`field ${f.full ? "f--full" : ""} ${err ? "is-error" : ""}`}
                  >
                    <label htmlFor={`ct-${f.name}`} className="mono">
                      {f.label}
                      {f.rules?.required && <span aria-hidden="true"> *</span>}
                    </label>
                    {f.multiline ? (
                      <textarea rows={4} {...common} />
                    ) : (
                      <input type={f.type || "text"} {...common} />
                    )}
                    {err && <span className="field__err mono">{err.message}</span>}
                  </div>
                );
              })}
            </div>

            <div className="captcha">
              <div className="captcha__clip">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_KEY}
                  theme="dark"
                  onChange={(v) => setCaptcha(!!v)}
                  onExpired={() => setCaptcha(false)}
                  ref={captchaRef}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary form__submit"
              disabled={!isValid || !captcha || sending}
            >
              {sending ? "Sending…" : "Send message"}
              {!sending && <ArrowRight className="i" />}
            </button>

            {status.state === "success" && (
              <p className="form__status form__status--ok" role="status">
                <Check /> {status.message}
              </p>
            )}
            {status.state === "error" && (
              <p className="form__status form__status--err" role="alert">
                {status.message}
              </p>
            )}
            <p className="form__fine mono">
              Protected by reCAPTCHA. We only use your details to reply to you.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
